import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useCompany } from "@/lib/company";
import { kes, today } from "@/lib/money";
import { Page, Btn, Table, Tr, Td, NumIn, TextIn, Field, Loading } from "@/components/kit";

const input = "min-h-[40px] rounded-md border border-slate-300 bg-white px-2 text-sm text-slate-800";
const num = (v) => Number(String(v || "").replace(/,/g, "")) || 0;
const blank = (station = "") => ({ k: Math.random().toString(36).slice(2), account_id: "", debit: "", credit: "", memo: "", station });
// a branch's own account (key "cash@Thome") always belongs to that branch
const ownBranch = (a) => (a?.key && a.key.includes("@") ? a.key.split("@").pop() : "");

export default function NewJournal() {
  const { company } = useCompany();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [accounts, setAccounts] = useState(null);
  const [kind, setKind] = useState(params.get("kind") === "opening" ? "opening" : "journal");
  const [date, setDate] = useState(params.get("kind") === "opening" ? "" : today());
  const [memo, setMemo] = useState(params.get("kind") === "opening" ? "Opening balances" : "");
  const [ref, setRef] = useState("");
  const [lines, setLines] = useState([blank(), blank()]);
  const [branch, setBranch] = useState("");
  const branches = company?.stations || [];
  const byBranch = branches.length > 1;
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!company) return;
    api.get(`/companies/${company.id}/accounts`).then(({ data }) => setAccounts(data.filter((a) => a.active !== false)));
    if (kind === "opening") setDate((d) => d || company.books_start);
  }, [company, kind]);
  if (!accounts) return <Page title="New journal"><Loading /></Page>;
  const set = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? {
    ...l, [k]: v, ...(k === "debit" && v ? { credit: "" } : {}), ...(k === "credit" && v ? { debit: "" } : {}),
    ...(k === "account_id" && ownBranch(accounts.find((a) => a.id === v)) ? { station: ownBranch(accounts.find((a) => a.id === v)) } : {}),
  } : l)));
  // the branch at the top fills every line, except a branch's own account (that stays with its branch)
  const setAll = (b) => {
    setBranch(b);
    setLines((ls) => ls.map((l) => (ownBranch(accounts.find((a) => a.id === l.account_id)) ? l : { ...l, station: b })));
  };
  const dr = lines.reduce((a, l) => a + num(l.debit), 0);
  const cr = lines.reduce((a, l) => a + num(l.credit), 0);
  const off = Math.round((dr - cr) * 100) / 100;
  const save = async () => {
    setBusy(true);
    try {
      await api.post(`/companies/${company.id}/journals`, {
        date, memo, ref, kind,
        lines: lines.filter((l) => l.account_id && (num(l.debit) || num(l.credit))).map((l) => ({ account_id: l.account_id, debit: num(l.debit), credit: num(l.credit), memo: l.memo, station: l.station || null })),
      });
      toast.success("Journal posted");
      navigate("/journals");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Page title={kind === "opening" ? "Opening balances" : "New journal"} back={{ to: "/journals", label: "Journals" }}>
      <div className={`mt-4 grid gap-3 ${byBranch ? "max-w-4xl sm:grid-cols-[160px_160px_1fr_200px]" : "max-w-3xl sm:grid-cols-[160px_160px_1fr]"}`}>
        <Field label="Date"><input type="date" className={input} value={date} onChange={(e) => setDate(e.target.value)} /></Field>
        <Field label="Reference"><TextIn label="Reference" value={ref} onChange={setRef} className="min-h-[40px]" /></Field>
        <Field label="Details"><TextIn label="Details" value={memo} onChange={setMemo} className="min-h-[40px]" /></Field>
        {byBranch && (
          <Field label="Branch (all lines)">
            <select className={input} value={branch} onChange={(e) => setAll(e.target.value)}>
              <option value="">Head office</option>
              {branches.map((b) => <option key={b} value={b}>{b}</option>)}
            </select>
          </Field>
        )}
      </div>
      {byBranch && <p className="mt-2 max-w-4xl text-sm text-[#5F5C58]">Put each cost or income against the branch it belongs to, so each site&apos;s profit &amp; loss is right. Lines can differ — e.g. one salary payment split across the three sites. Head office is for what belongs to no single site.</p>}
      <label className="mt-3 flex items-center gap-2 text-sm"><input type="checkbox" checked={kind === "opening"} onChange={(e) => setKind(e.target.checked ? "opening" : "journal")} /> Opening balance</label>
      <Table minWidth={byBranch ? 940 : 760} cols={[{ label: "Account" }, ...(byBranch ? [{ label: "Branch" }] : []), { label: "Line note" }, { label: "Debit", align: "right" }, { label: "Credit", align: "right" }, { label: "" }]}
        foot={<tr>
          <td className="px-2 py-2.5" colSpan={byBranch ? 3 : 2}>{off === 0 && dr > 0 ? <span className="text-[#1F6B3A]">Balanced</span> : off ? <span className="text-[#A11D1D]">Off by {kes(Math.abs(off))}</span> : "Total"}</td>
          <td className="px-2 py-2.5 text-right tabular-nums">{kes(dr)}</td><td className="px-2 py-2.5 text-right tabular-nums">{kes(cr)}</td><td />
        </tr>}>
        {lines.map((l, i) => (
          <Tr key={l.k}>
            <Td>
              <select aria-label="Account" className={`${input} w-full min-w-[260px]`} value={l.account_id} onChange={(e) => set(i, "account_id", e.target.value)}>
                <option value="">—</option>
                {accounts.map((a) => <option key={a.id} value={a.id}>{a.code} {a.name}</option>)}
              </select>
            </Td>
            {byBranch && (
              <Td>
                <select aria-label="Branch" className={`${input} w-full min-w-[150px]`} value={l.station} disabled={!!ownBranch(accounts.find((a) => a.id === l.account_id))}
                  onChange={(e) => set(i, "station", e.target.value)}>
                  <option value="">Head office</option>
                  {branches.map((b) => <option key={b} value={b}>{b}</option>)}
                </select>
              </Td>
            )}
            <Td><TextIn label="Line note" value={l.memo} onChange={(v) => set(i, "memo", v)} /></Td>
            <Td right><NumIn label="Debit" value={l.debit} onChange={(v) => set(i, "debit", v)} width="w-36" /></Td>
            <Td right><NumIn label="Credit" value={l.credit} onChange={(v) => set(i, "credit", v)} width="w-36" /></Td>
            <Td>{lines.length > 2 && <button type="button" aria-label="Remove line" className="min-h-[34px] px-2 text-slate-400 hover:text-rose-600" onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))}>×</button>}</Td>
          </Tr>
        ))}
      </Table>
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <Btn kind="link" onClick={() => setLines((ls) => [...ls, blank(branch)])}>+ Add line</Btn>
        <div className="flex-1" />
        <Btn onClick={() => navigate("/journals")}>Cancel</Btn>
        <Btn kind="primary" disabled={busy || off !== 0 || dr === 0 || !memo.trim() || !date} onClick={save}>Post journal</Btn>
      </div>
    </Page>
  );
}
